import type React from "react"

const READ_ONLY_INTERACTIVE_ATTRIBUTE = "data-read-only-interactive"

/** Spread onto a control that stays usable inside a read-only container, such as a copy button. */
export const readOnlyInteractiveProps = {
  [READ_ONLY_INTERACTIVE_ATTRIBUTE]: "",
} as const

// Links only navigate, so they stay usable by pointer and keyboard.
const INTERACTIVE_SELECTOR = `[${READ_ONLY_INTERACTIVE_ATTRIBUTE}], a[href]`

const isFromInteractiveControl = (event: React.SyntheticEvent): boolean =>
  event.target instanceof Element &&
  event.target.closest(INTERACTIVE_SELECTOR) !== null

const swallow = (event: React.SyntheticEvent): void => {
  if (isFromInteractiveControl(event)) {
    return
  }
  event.preventDefault()
  event.stopPropagation()
}

const isolate = (event: React.SyntheticEvent): void => {
  if (isFromInteractiveControl(event)) {
    return
  }
  event.stopPropagation()
}

// Matched by physical key so the shortcuts work on non-Latin keyboard layouts.
const COPY_SHORTCUT_CODES = new Set(["KeyA", "KeyC"])

// Focus can land inside the region on pointer-down, so these keep their default to let focus leave and dialogs close.
const PASS_THROUGH_KEYS = new Set(["Tab", "Escape"])

const swallowKeyDown = (event: React.KeyboardEvent): void => {
  const isCopyShortcut =
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    COPY_SHORTCUT_CODES.has(event.code)
  if (isCopyShortcut || PASS_THROUGH_KEYS.has(event.key)) {
    isolate(event)
    return
  }
  swallow(event)
}

type ReadOnlyInteractionGuardProps = Pick<
  React.DOMAttributes<HTMLElement>,
  | "onClickCapture"
  | "onPointerDownCapture"
  | "onMouseDownCapture"
  | "onKeyDownCapture"
  | "onPasteCapture"
  | "onCutCapture"
  | "onDropCapture"
>

/**
 * Capture-phase handlers that make a container's subtree non-interactive while leaving it scrollable and its text selectable and copyable, for read-only surfaces like version-history snapshots.
 *
 * A `disabled` fieldset only neutralizes native form controls; custom widgets (Radix sliders, `role="button"` tag chips, links, code editors) stay live and can reach their mutation handlers. `pointer-events: none` would block them all but also kills wheel/scroll and text selection. Spread these onto the read-only container instead: clicks, keystrokes, paste, cut, and drop are swallowed in the capture phase, so descendant handlers never fire and default navigation and editing are cancelled. Pointer-down only stops propagation, so the browser can still start a text selection, and Cmd/Ctrl+C, Cmd/Ctrl+A, Tab, and Escape keep their default behavior. Links and controls marked with `readOnlyInteractiveProps` are exempt.
 *
 * Returns no handlers when `active` is false, so callers can spread it unconditionally on a container that is only sometimes read-only.
 */
export function readOnlyInteractionGuardProps(
  active = true,
): ReadOnlyInteractionGuardProps {
  if (!active) {
    return {}
  }
  return {
    onClickCapture: swallow,
    onPointerDownCapture: isolate,
    onMouseDownCapture: isolate,
    onKeyDownCapture: swallowKeyDown,
    onPasteCapture: swallow,
    onCutCapture: swallow,
    onDropCapture: swallow,
  }
}
